import { motion, AnimatePresence } from "motion/react";
import { useRegisterSW } from "virtual:pwa-register/react";

/**
 * "A new version is ready" prompt for the PWA service worker.
 *
 * The worker used to be `registerType: 'autoUpdate'`, which activates the new
 * build immediately and clears the outdated precache underneath whatever tabs
 * are open. Those tabs then reach for chunks that no longer exist and every
 * navigation breaks mid-session. On `'prompt'` the new worker waits, the open
 * tab keeps the exact assets it booted with, and the swap happens on a reload
 * the user chose — which is also the moment it's safe.
 */

/** How often a long-lived tab checks whether a newer build has shipped. */
const UPDATE_POLL_MS = 30 * 60 * 1000;

export function UpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      // Without this a tab left open for days only notices a new deploy on its
      // next navigation request — too late if its chunks are already gone.
      setInterval(() => {
        void registration.update().catch(() => {});
      }, UPDATE_POLL_MS);
    },
  });

  return (
    <AnimatePresence>
      {needRefresh && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 rounded-2xl bg-card max-w-[min(92vw,480px)] px-6 py-3.5 border border-accent/30 shadow-xl shadow-accent/10"
          style={{ bottom: "calc(2rem + var(--mobile-tab-bar))" }}
        >
          <span
            className="text-accent"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: "0.95rem",
              fontStyle: "italic",
            }}
          >
            A new version of Burrow is ready.
          </span>
          <button
            onClick={() => void updateServiceWorker(true)}
            className="shrink-0 -my-1 px-3.5 py-1.5 rounded-lg bg-accent text-accent-foreground hover:opacity-90 active:scale-95 transition-all"
            style={{ fontSize: "0.82rem", fontWeight: 600 }}
          >
            Reload
          </button>
          <button
            onClick={() => setNeedRefresh(false)}
            className="shrink-0 text-muted-foreground hover:text-primary transition-colors"
            style={{ fontSize: "0.8rem" }}
          >
            Later
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
